import type { Account } from '@xinfer/api-client';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DescriptionItem,
  DescriptionList,
  PageHeader,
  SectionHeader,
  usePageTitle,
} from '@xinfer/ui';
import { CircleAlert, Hourglass, RefreshCw, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * The plan's limits, in the terms the gate actually enforces them.
 *
 * Concrete numbers are resolved by the control plane and pushed to the serving
 * path, so what is described here is what will actually happen to a request.
 */
export function Limits({ account }: { account: Account }) {
  usePageTitle('Limits');

  return (
    <>
      <PageHeader
        title="Limits"
        description="What the gate enforces on this plan, and why each cap exists."
        meta={<Badge variant="outline">{account.plan}</Badge>}
      />

      <Card>
        <CardHeader>
          <CardTitle>Enforced limits</CardTitle>
          <CardDescription>
            Each of these protects a specific resource. The concrete numbers are resolved by the
            control plane and pushed to the serving path.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DescriptionList>
            <DescriptionItem label="Concurrent requests">
              KV memory directly. This is the binding resource: the node holds a bounded number of
              decoding streams at once regardless of how fast anyone is typing.
            </DescriptionItem>
            <DescriptionItem label="Maximum context">
              The attention path in use. A prompt longer than this is refused rather than served
              slowly, because the slow path would take minutes and hold KV the whole time.
            </DescriptionItem>
            <DescriptionItem label="Concurrent long sessions">
              Capped separately from ordinary requests. One 512k-context session is worth about
              twenty ordinary ones, so a single quota covering both would misprice it.
            </DescriptionItem>
            <DescriptionItem label="Default output cap">
              Applied when a request does not set <code className="font-mono">max_tokens</code>{' '}
              itself.
            </DescriptionItem>
            <DescriptionItem label="Credit windows">
              A separate limit of a different kind: how much has been <em>spent</em>, over 5 hours,
              7 days and 30 days. The three caps and the current headroom are on the{' '}
              <Link className="font-medium text-foreground underline" to="/credits">
                plan page
              </Link>
              .
            </DescriptionItem>
          </DescriptionList>
        </CardContent>
      </Card>

      <div className="mt-8">
        <SectionHeader
          title="Two limits, both counted per account"
          description="They protect different things, and they fail differently."
        />
        <Card>
          <CardContent className="pt-6 text-sm leading-relaxed text-muted-foreground">
            <p>
              The limits above bound what is happening <em>right now</em> — how much KV memory this
              account holds at one instant. They clear the moment a request finishes.
            </p>
            <p className="mt-3">
              Credit windows bound what has been <em>sold</em>, over hours and days. They clear
              only as usage ages out of the window, so hitting one is not something a retry loop
              can wait out.
            </p>
            <p className="mt-3">
              Both are counted per account, never per key. Minting a second key does not buy a
              second allowance — a key is a credential, not a wallet.
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="mt-8">
        <SectionHeader
          title="What happens when you exceed one"
          description="Three different answers, and only one of them is worth retrying blindly."
        />

        <div className="space-y-3">
          <Alert variant="warning">
            <RefreshCw />
            <div>
              <AlertTitle>
                <code className="font-mono">429 quota_exceeded</code> — concurrency
              </AlertTitle>
              <AlertDescription>
                Safe to retry with backoff, and usually within milliseconds. The account is at its
                concurrency limit right now; nothing about the request was wrong.
              </AlertDescription>
            </div>
          </Alert>

          <Alert variant="warning">
            <Hourglass />
            <div>
              <AlertTitle>
                <code className="font-mono">429 credits_exhausted</code> — credit window
              </AlertTitle>
              <AlertDescription>
                Also a <code className="font-mono">429</code>, and not the same thing. A credit
                window can be hours from reopening, so this response carries a{' '}
                <code className="font-mono">Retry-After</code> header and the message names which
                window bound. Retrying before then will be refused again — branch on{' '}
                <code className="font-mono">error.code</code>, not on the status.
              </AlertDescription>
            </div>
          </Alert>

          <Alert variant="danger">
            <XCircle />
            <div>
              <AlertTitle>
                <code className="font-mono">400</code> — prompt over the context cap
              </AlertTitle>
              <AlertDescription>
                Retrying will not help. The prompt has to get shorter, or the plan has to allow a
                longer context.
              </AlertDescription>
            </div>
          </Alert>

          <Alert variant="info">
            <CircleAlert />
            <div>
              <AlertTitle>
                <code className="font-mono">503</code> — the service is at capacity
              </AlertTitle>
              <AlertDescription>
                Nothing is wrong with the request. The fleet has no room for it at this moment.
              </AlertDescription>
            </div>
          </Alert>
        </div>
      </div>
    </>
  );
}
